import { useState, useEffect } from "react";
import {
  getInstallPrompt,
  callInstallPrompt,
  onInstallPromptChange,
  isIOS,
  isStandalone,
} from "../utils/installPrompt";

/**
 * '홈 화면에 추가' 띠.
 *
 * 예전에는 작은 '설치' 버튼 하나만 눌렸고, 그 버튼은 대개 나오지도 않았다.
 * 이벤트를 놓쳐서다(utils/installPrompt 에 적어두었다). 지금은 띠 전체가
 * 눌리고, 눌렀을 때 할 수 있는 일이 기기마다 다르다.
 *
 *   안드로이드·크롬   설치 창을 바로 띄운다.
 *   아이폰·아이패드   띄울 수 없다. 애플이 코드로 부르는 길을 열어두지
 *                     않았다. 대신 어디를 눌러야 하는지 펼쳐서 보여준다.
 *
 * 부를 수도 없고 아이폰도 아니면(이미 설치했거나 조건이 안 맞는 브라우저)
 * 띠를 아예 내보내지 않는다 — 눌러도 아무 일 없는 띠는 없는 것만 못하다.
 */
export default function InstallBanner() {
  const [prompt, setPrompt] = useState(() => getInstallPrompt());
  const [dismissed, setDismissed] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const ios = isIOS();
  const standalone = isStandalone();

  useEffect(() => {
    if (standalone) return;
    if (sessionStorage.getItem("install-banner-dismissed")) {
      setDismissed(true);
      return;
    }
    /* 이미 받아둔 것이 있으면 그것부터, 뒤늦게 오면 그때 다시. */
    setPrompt(getInstallPrompt());
    return onInstallPromptChange(setPrompt);
  }, [standalone]);

  /* 설치가 끝나면 prompt 가 비워진다. 안내를 펼쳐둔 채로 두지 않는다. */
  useEffect(() => {
    if (!prompt) setShowGuide((v) => (ios ? v : false));
  }, [prompt, ios]);

  if (standalone || dismissed) return null;
  /* 할 수 있는 일이 없으면 내보내지 않는다 */
  if (!prompt && !ios) return null;

  const handleTap = async () => {
    if (prompt) {
      const called = await callInstallPrompt();
      if (called) setDismissed(true);
      return;
    }
    /* 아이폰 — 부를 수 없으니 어디를 눌러야 하는지 알려준다 */
    setShowGuide((v) => !v);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem("install-banner-dismissed", "1");
    setDismissed(true);
  };

  const desc = ios
    ? showGuide
      ? "아래 안내를 따라 해주세요"
      : "눌러서 추가하는 방법 보기"
    : "눌러서 바로 추가하기";

  return (
    <div style={styles.banner}>
      {/* 띠 전체가 버튼이다. 글씨를 눌러도 되게 하려는 것이다. */}
      <button
        type="button"
        style={styles.tap}
        onClick={handleTap}
        aria-expanded={ios ? showGuide : undefined}
      >
        <span style={styles.icon} aria-hidden="true">
          &#127759;
        </span>
        <span style={styles.text}>
          <strong style={styles.title}>MUROO를 홈 화면에 추가하세요</strong>
          <span style={styles.desc}>{desc}</span>
        </span>
      </button>

      <button
        type="button"
        style={styles.closeBtn}
        onClick={handleDismiss}
        aria-label="닫기"
      >
        &#x2715;
      </button>

      {/* 아이폰 안내. 사파리의 공유 단추는 판마다 아래에도 위에도 있어서
          '아래쪽' 이라고 단정하지 않는다. */}
      {ios && showGuide && (
        <div style={styles.guide}>
          <div style={styles.step}>
            <span style={styles.num}>1</span>
            <span>
              사파리의 <strong style={styles.hl}>공유</strong> 단추를 누릅니다
              <span style={styles.share} aria-hidden="true">
                &#x2191;
              </span>
            </span>
          </div>
          <div style={styles.step}>
            <span style={styles.num}>2</span>
            <span>
              목록을 내려 <strong style={styles.hl}>홈 화면에 추가</strong>를
              누릅니다
            </span>
          </div>
          <div style={styles.note}>
            아이폰은 애플 정책상 이 단계를 앱이 대신 눌러줄 수 없어요
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  banner: {
    position: "fixed",
    /* 홈에서만 뜨므로 아래 탭 막대와 떠 있는 두 버튼(피드백·인스타)을 모두
       피해야 한다. BottomTabs.css 에 적힌 대로 탭 막대 위가
       4.4rem + 안전영역 + 주소창이 걸친 높이(--tabbar-shift) 이고,
       그 위로 동그란 버튼이 36px 씩 두 개 쌓인다. 겹치면 둘 다 눌리지 않는다. */
    bottom:
      "calc(4.4rem + 36px + .5rem + 36px + .6rem + env(safe-area-inset-bottom, 0px) + var(--tabbar-shift, 0px))",
    left: "1rem",
    right: "1rem",
    maxWidth: "420px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: ".75rem",
    padding: ".85rem 1rem",
    background: "rgba(13,18,53,.92)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(123,108,212,.35)",
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(0,0,0,.5)",
    zIndex: 300,
    animation: "fadeUp .4s ease-out",
  },
  /* 버튼이지만 생김새는 글씨 그대로여야 한다 */
  tap: {
    display: "flex",
    alignItems: "center",
    gap: ".7rem",
    flex: 1,
    minWidth: 0,
    background: "none",
    border: "none",
    padding: 0,
    color: "inherit",
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
  },
  icon: { fontSize: "1.6rem", flexShrink: 0 },
  text: { display: "flex", flexDirection: "column", gap: ".15rem", minWidth: 0 },
  title: { fontSize: ".85rem", color: "#f5f0e8", letterSpacing: ".05em" },
  desc: {
    fontSize: ".7rem",
    color: "rgba(245,240,232,.5)",
    fontFamily: "'DM Mono', monospace",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "rgba(245,240,232,.4)",
    fontSize: ".9rem",
    cursor: "pointer",
    padding: ".2rem",
    lineHeight: 1,
    flexShrink: 0,
  },
  guide: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: ".45rem",
    paddingTop: ".7rem",
    borderTop: "1px solid rgba(245,240,232,.1)",
  },
  step: {
    display: "flex",
    alignItems: "flex-start",
    gap: ".5rem",
    fontSize: ".76rem",
    color: "rgba(245,240,232,.78)",
    lineHeight: 1.6,
  },
  num: {
    flexShrink: 0,
    width: "1.1rem",
    height: "1.1rem",
    marginTop: ".2rem",
    borderRadius: "50%",
    background: "rgba(123,108,212,.3)",
    color: "#f5f0e8",
    fontSize: ".64rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  hl: { color: "#c9a84c", fontWeight: 500 },
  share: { marginLeft: ".25rem", color: "#c9a84c" },
  note: {
    fontSize: ".68rem",
    color: "rgba(245,240,232,.4)",
    lineHeight: 1.6,
  },
};
